import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import type { Rol } from "@/types";
import { useAuth } from "@/hooks/useAuth";
import { CambiarPasswordPage } from "@/pages/CambiarPassword";

export function RequiereAuth({
  children,
  rol,
}: {
  children: ReactNode;
  /** Rol mínimo exigido. Sin él, basta estar autenticado. */
  rol?: Rol;
}) {
  const { sesion, cargando, debeCambiarPassword } = useAuth();
  const location = useLocation();

  // Mientras se restablece la sesión desde la cookie (/auth/me), no
  // decidimos aún: redirigir a /login aquí haría un flash al recargar
  // una ruta profunda estando autenticado.
  if (cargando) {
    return (
      <div
        className="min-h-screen grid place-items-center"
        style={{ background: "var(--bg)", color: "var(--ink-3)" }}
      >
        Cargando…
      </div>
    );
  }

  if (!sesion) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // La contraseña provisional la eligió un administrador: hasta rotarla,
  // no se accede a ninguna ruta protegida. Se interpone aquí, y no con
  // una redirección, para que no haya ruta que saltarse escribiendo la URL.
  if (debeCambiarPassword) {
    return <CambiarPasswordPage />;
  }
  // Guarda de rol. El backend ya protege cada endpoint, así que esto es
  // sólo para no mostrar una pantalla que fallaría con 403 al cargar.
  if (rol && !cumpleRol(sesion.rol, rol)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Jerarquía estricta: administrador ⊇ supervisor ⊇ analista.
// `sistema` es ortogonal (conectores) y no accede al SPA.
const ORDEN: Record<Rol, number> = {
  sistema: 0,
  analista: 1,
  supervisor: 2,
  administrador: 3,
};

function cumpleRol(actual: Rol, minimo: Rol): boolean {
  return (ORDEN[actual] ?? 0) >= (ORDEN[minimo] ?? 99);
}
